// Maps EWT's wire audit event onto kore's canonical `AuditEvent`, and holds
// the EWT-specific props kore's audit components take: chip labels, detail
// renderers for the custom variants, timeline summaries, target type labels.

import { AuditDetailsView } from "@pej/kore/components";
import type { AuditActionMeta, AuditDetails, AuditEvent } from "@pej/kore/types";
import type { ReactNode } from "react";
import type { AuditAction, AuditEventRs, AuditTargetType } from "@/api/types";
import { formatTargetType, summarizeDetails } from "./summary";

/** Near-identity: kore types optionals as `?:`, the wire sends `null`. */
export function toKoreAuditEvent(event: AuditEventRs): AuditEvent {
    const details =
        event.details.type === "Deleted"
            ? { ...event.details, reason: event.details.reason ?? undefined }
            : event.details;
    return {
        ...event,
        actor: event.actor
            ? {
                  ...event.actor,
                  displayName: event.actor.displayName ?? undefined,
                  email: event.actor.email ?? undefined,
              }
            : null,
        source: event.source ? { ...event.source, service: event.source.service ?? undefined } : null,
        details: details as AuditDetails,
    };
}

type CustomAction = Exclude<AuditAction, "Created" | "Updated" | "Deleted">;

/** Merged over kore's Created/Updated/Deleted defaults; same map for table and sheet. */
export const auditActionMeta: Record<string, AuditActionMeta> = {
    DisplayTokenRotated: { label: "Token rotated", tone: "accent" },
    ServiceKeyIssued: { label: "Service key issued", tone: "accent" },
} satisfies Record<CustomAction, AuditActionMeta>;

export const AUDIT_ACTION_LABELS: Record<AuditAction, string> = {
    Created: "Created",
    Updated: "Updated",
    Deleted: "Deleted",
    DisplayTokenRotated: "Token rotated",
    ServiceKeyIssued: "Service key issued",
};

const TARGET_TYPE_LABELS: Record<AuditTargetType, string> = {
    Workspace: "Workspace",
    Location: "Location",
    Screen: "Screen",
    DesignTemplate: "Template",
};

export function auditTargetTypeLabel(type: string): string {
    return TARGET_TYPE_LABELS[type as AuditTargetType] ?? formatTargetType(type);
}

function Sentence({ children }: { children: ReactNode }) {
    return <p className="text-sm text-kore-text">{children}</p>;
}

// The open `AuditDetails` arm means these payloads are read, not trusted.
type LooseDetails = { reason?: unknown; fields?: unknown; newAccount?: unknown };

function DeletedDetails({ details, event }: { details: AuditDetails; event: AuditEvent }) {
    const { reason: rawReason, fields: rawFields } = details as LooseDetails;
    const reason = typeof rawReason === "string" && rawReason.length > 0 ? rawReason : null;
    const fields = Array.isArray(rawFields) ? rawFields : [];
    return (
        <div className="flex flex-col gap-3">
            {reason ? <Sentence>{reason}</Sentence> : null}
            {/* kore's built-in Deleted view drops `fields`; reuse its Created table for them. */}
            {fields.length > 0 ? <AuditDetailsView event={{ ...event, details: { type: "Created", fields } }} /> : null}
            {!reason && fields.length === 0 ? <Sentence>No details recorded.</Sentence> : null}
        </div>
    );
}

/** Keyed by `details.type`; replaces kore's renderer for that type. */
export const auditDetailRenderers: Record<string, (details: AuditDetails, event: AuditEvent) => ReactNode> = {
    Deleted: (details, event) => <DeletedDetails details={details} event={event} />,
    DisplayTokenRotated: () => (
        <Sentence>The screen's display URL was regenerated. The previous URL stopped working.</Sentence>
    ),
    ServiceKeyIssued: (details) =>
        (details as LooseDetails).newAccount === true ? (
            <Sentence>A new service account was created for this location and its posting service key issued.</Sentence>
        ) : (
            <Sentence>
                The posting service configuration was downloaded for the location's existing service account.
            </Sentence>
        ),
};

/** Timeline and caused-by rows read "<actor> <summary>", so "—" falls back to the action label. */
export function summarizeKoreEvent(event: AuditEvent): string {
    const summary = summarizeDetails(event.details);
    if (summary !== "—") return summary;
    const label = AUDIT_ACTION_LABELS[event.details.type as AuditAction] ?? formatTargetType(event.details.type);
    return label.toLowerCase();
}
